import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Icon, Menu, Switch } from "antd";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as DashboardIcon } from "../../assets/images/dashboard-icon.svg";
import { ReactComponent as CalendarIcon } from "../../assets/images/calendar_icon.svg";
import { ReactComponent as VisitsIcon } from "../../assets/images/visits-icon.svg";
import { ReactComponent as PatientsIcon } from "../../assets/images/patients-icon.svg";
import { ReactComponent as RefDoctorsIcon } from "../../assets/images/ref_doctors-icon.svg";
import { ReactComponent as OrganisationsIcon } from "../../assets/images/organisations-icon.svg";
import { ReactComponent as LineupIcon } from "../../assets/images/lineup-icon.svg";
import { ReactComponent as TechnicianWorklistIcon } from "../../assets/images/technician_worklist-icon.svg";
import { ReactComponent as QuickCodesIcon } from "../../assets/images/quick_codes-icon.svg";
import { ReactComponent as AccountsIcon } from "../../assets/images/account-icon.svg";
import { ReactComponent as SettingsIcon } from "../../assets/images/settings-icon.svg";
import { ReactComponent as LogoutIcon } from "../../assets/images/logout.svg";
import { ReactComponent as UnreportedIcon } from "../../assets/images/unreported-icon.svg";
import { ReactComponent as ConfigurationIcon } from "../../assets/images/configuration-icon.svg";
import CustomScrollbars from "util/CustomScrollbars";
import SidebarLogo from "./SidebarLogo";
import Auxiliary from "util/Auxiliary";
import IntlMessages from "util/IntlMessages";
import ChatbotButton from "../../components/EsterButton";
import { setThemeType, onNavStyleChange } from "appRedux/actions";
import { userSignOut } from "../../appRedux/actions/Auth";
import {
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
  THEME_TYPE_LITE,
  THEME_TYPE_DARK,  
} from "constants/ThemeSetting";
import { isFeatureEnabled } from "util/feature-toogle";
import { useState } from "react";
import WhatsNewBanner from "../../components/common/whtasNewBanner";

const { ItemGroup } = Menu;
const SubMenu = Menu.SubMenu;

const SidebarContent = () => {
  const [isNewsOpen, setIsNewsOpen] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const { themeType, navStyle, authUser, navStyleFromState } = useSelector(
    ({ settings, auth }) => ({
      navStyle: settings.navStyle,
      themeType: settings.themeType,
      authUser: auth.authUser,
    })
  );

  const handleLogout = () => {
    dispatch(userSignOut());
  };

  const getActiveKey = (path) => {
    // Handle specific mappings
    if (path.includes("profile-settings")) return "profile/settings";
    
    // Ensure the menu stays active when viewing nested routes (e.g. /receptionist/visits/123)
    const parts = path.split("/").filter(Boolean);
    if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
    return path.substr(1);
  };

  const selectedKeys = getActiveKey(location.pathname);
  const defaultOpenKeys = selectedKeys.split("/")[1];

  const showLegacyAccounts = isFeatureEnabled("REACT_APP_FEATURE_ACCOUNTS_LEGACY");

  const onThemeChange = (checked) => {
    dispatch(setThemeType(checked ? THEME_TYPE_DARK : THEME_TYPE_LITE));
    localStorage.setItem("THEME_TYPE", checked ? THEME_TYPE_DARK : THEME_TYPE_LITE);
  };

  const getNavStyleSubMenuClass = (navStyle) => {
    if (navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR) {
      return "gx-no-header-submenu-popup";
    }
    return "";
  };

  // ----- MENUS BASED ON ROLES -----
  const renderMenu = () => {
    const latestFeaturesMenuItem = (
      <Menu.Item key="latest-features" onClick={() => setIsNewsOpen(true)}>
        <Icon type="rocket" style={{ color: '#4A90E2' }} /> {/* Changed to Rocket Icon */}
        <span>Latest Features</span>
      </Menu.Item>
    );

    if (authUser?.role === "ADMIN") {
      return (
        <Menu
          defaultOpenKeys={[defaultOpenKeys]}
          selectedKeys={[selectedKeys]}
          theme={themeType === THEME_TYPE_LITE ? "light" : "dark"}
          mode="inline"
        >
          <Menu.Item key="admin/dashboard">
            <Link to="/admin/dashboard">
              <DashboardIcon />
              <IntlMessages id="sidebar.dashboard" />
            </Link>
          </Menu.Item>

          <ItemGroup
            key="workflows"
            title={<span className="menu-section-title">Workflows</span>}
          >
              <Menu.Item key="admin/configuration">
                <Link to="/admin/configuration">
                  <ConfigurationIcon />
                  <IntlMessages id="sidebar.configuration" />
                </Link>
              </Menu.Item>
              <Menu.Item key="admin/hl7-messages">
                <Link to="/admin/hl7-messages">
                  <i className="icon icon-mail-open" />
                  <IntlMessages id="sidebar.hl7-messages" />
                </Link>
              </Menu.Item>

              {
                authUser.mips && authUser.mips.length && (authUser.mips[0].siteCode === "TMU" || authUser.mips[0].siteCode === "GMU") &&(<Menu.Item key="admin/radian-report">
                  <Link to="/admin/radian-report">
                    <i className="icon icon-crypto" />
                    <IntlMessages id="sidebar.statistical-report.radian-report" />
                  </Link>
                </Menu.Item>)
              }
          </ItemGroup>

          {/* Section: HL7 Messages       
          <ItemGroup
            key="hl7messages"
            title={<span className="menu-section-title">HL7 Messages</span>}
          >
            <Menu.Item key="admin/hl7-sent-messages">
              <Link to="/admin/hl7-sent-messages">
                <i className="icon icon-mail-open" />
                <IntlMessages id="sidebar.hl7sentmessages" />
              </Link>
            </Menu.Item>
            <Menu.Item key="admin/hl7-received-messages">
              <Link to="/admin/hl7-received-messages">
                <i className="icon icon-message" />
                <IntlMessages id="sidebar.hl7receivedmessages" />
              </Link>
            </Menu.Item>
          </ItemGroup>
         
          {/* <SubMenu key="Statistical Reports" className={getNavStyleSubMenuClass(navStyle)}
            title={<span> <i className="icon icon-copy" />
              <IntlMessages id="sidebar.statistical-report" /></span>}>
            <Menu.Item key="admin/statistical-report/visit-report">
              <Link to="/admin/statistical-report/visit-report">
                <i className="icon icon-crypto" />
                <IntlMessages id="sidebar.statistical-report.visit-report" />
              </Link>
            </Menu.Item>
            {authUser.mips && authUser.mips.length && authUser.mips[0].siteCode === "TMU" && (<Menu.Item key="admin/radian-report">
              <Link to="/admin/radian-report">
                <i className="icon icon-crypto" />
                <IntlMessages id="sidebar.statistical-report.radian-report" />
              </Link>
            </Menu.Item>)}
          </SubMenu> */}
          {/* <Menu.Item key="admin/scan-types">
            <Link to="/admin/scan-types"><i className="icon icon-tasks" />
              <IntlMessages id="sidebar.scanTypes" /></Link>
          </Menu.Item> */}

            <Menu.Item key="admin/accounts">
                <Link to="/admin/accounts"><Icon className="custom-sidebar-icon" type="account-book" />
                <IntlMessages id="sidebar.receptionist.Accounts" /></Link>
            </Menu.Item>

          {showLegacyAccounts && (
            <Menu.Item
              key="admin/accounts-legacy">
              <Link to="/admin/accounts-legacy">
                <Icon className="custom-sidebar-icon" type="account-book" />
                <IntlMessages id="sidebar.receptionist.AccountsLegacy" />
              </Link>
            </Menu.Item>
          )}
          {/*
          <Menu.Item key="admin/hl7-sent-messages">
            <Link to="/admin/hl7-sent-messages"><i className="icon icon-timeline-left-align" />
              <IntlMessages id="sidebar.hl7sentmessages" /></Link>
          </Menu.Item>

          <Menu.Item key="admin/hl7-received-messages">
            <Link to="/admin/hl7-received-messages"><i className="icon icon-widgets" />
              <IntlMessages id="sidebar.hl7receivedmessages" /></Link>
          </Menu.Item> */}

            {/* ADDED UPDATES SECTION FOR ADMIN */}
          <ItemGroup key="admin-updates" title={<span className="menu-section-title">Updates</span>}>
             {latestFeaturesMenuItem}
          </ItemGroup>


          {/* Section: Settings */}
          <ItemGroup
            key="settings"
            title={<span className="menu-section-title">Settings</span>}
          >
            <Menu.Item className="logout-item" key="profile/logout" onClick={handleLogout}>
                <LogoutIcon />
                <span>Logout</span>
            </Menu.Item>
          </ItemGroup>
        </Menu>
      );
    }

    if (authUser?.role === "RECEPTIONIST") {
      return (
        <Menu
          defaultOpenKeys={[defaultOpenKeys]}
          selectedKeys={[selectedKeys]}
          theme={themeType === THEME_TYPE_LITE ? "light" : "dark"}
          mode="inline"
        >
          <Menu.Item key="receptionist/dashboard">
            <Link to="/receptionist/dashboard">
              <DashboardIcon />
              <IntlMessages id="sidebar.dashboard" />
            </Link>
          </Menu.Item>
          {/* Section: Appointments */}
          <ItemGroup
            key="appointments"
            title={<span className="menu-section-title">Appointments</span>}
          >
            <Menu.Item key="receptionist/calendar">
              <Link to="/receptionist/calendar">
                <CalendarIcon />
                <IntlMessages id="sidebar.receptionist.Calendar" />
              </Link>
            </Menu.Item>
            <Menu.Item key="receptionist/visits">
              <Link to="/receptionist/visits">
                <VisitsIcon />
                <IntlMessages id="sidebar.receptionist.Visits" />
              </Link>
            </Menu.Item>
            <Menu.Item key="receptionist/visits-pending-for-report">
              <Link to="/receptionist/visits-pending-for-report">
                <UnreportedIcon />
                <IntlMessages id="sidebar.receptionist.VisitsPendingForReport" />
              </Link>
            </Menu.Item>
          </ItemGroup>

          {/* Section: Patient Management */}
          <ItemGroup
            key="market"
            title={<span className="menu-section-title">Patient Management</span>}
          >
            <Menu.Item className="stroke" key="receptionist/patients">
              <Link to="/receptionist/patients">
                <PatientsIcon />
                <IntlMessages id="sidebar.patients" />
              </Link>
            </Menu.Item>

            <Menu.Item key="receptionist/patients">
              <Link to="/receptionist/aob">
                <UnreportedIcon />
                <IntlMessages id="sidebar.receptionist.MedicareAOB" />
              </Link>
            </Menu.Item>

            <Menu.Item className="stroke" key="receptionist/ref-doctors">
              <Link to="/receptionist/ref-doctors">
                <RefDoctorsIcon />
                <IntlMessages id="sidebar.refDoctors" />
              </Link>
            </Menu.Item>
            <Menu.Item key="receptionist/organisations">
              <Link to="/receptionist/organisations">
                <OrganisationsIcon />
                <IntlMessages id="sidebar.receptionist.Organisations" />
              </Link>
            </Menu.Item>
          </ItemGroup>

          {/* Section: Operations */}
          <ItemGroup
            key="service"
            title={<span className="menu-section-title">Operations</span>}
          >
              <Menu.Item key="receptionist/worklist">
                <Link to="/receptionist/worklist">
                  <TechnicianWorklistIcon />
                  <IntlMessages id="sidebar.receptionist.Worklist" />
                </Link>
              </Menu.Item>
              <Menu.Item key="receptionist/lineup">
                <Link to="/receptionist/lineup">
                  <LineupIcon />
                  <IntlMessages id="sidebar.lineup" />
                </Link>
              </Menu.Item>
              <Menu.Item key="receptionist/quick-codes">
                <Link to="/receptionist/quick-codes">
                  <QuickCodesIcon />
                  <IntlMessages id="sidebar.receptionist.QuickCodes" /></Link>
              </Menu.Item>
              <Menu.Item key="receptionist/delivery-report">
                <Link to="/receptionist/delivery-report">
                  <TechnicianWorklistIcon />
                  <IntlMessages id="sidebar.receptionist.DeliveryReport" />
                </Link>
              </Menu.Item>
              {/* <Menu.Item key="receptionist/statistical-overview">
                <Link to="/receptionist/statistical-overview">
                  <TechnicianWorklistIcon />
                  <IntlMessages id="sidebar.receptionist.StatisticalOverview" />
                </Link>
              </Menu.Item> */}
          </ItemGroup>

          {/* 5. Add to Receptionist Menu (usually under Settings or Operations) */}
          <ItemGroup key="others" title={<span className="menu-section-title">Updates</span>}>
             {latestFeaturesMenuItem}
          </ItemGroup>

          {/* Section: Settings */}
          <ItemGroup
            key="accounts"
            title={<span className="menu-section-title">Settings</span>}
          >
            <Menu.Item key="receptionist/accounts">
              <Link to="/receptionist/accounts">
                <Icon className="custom-sidebar-icon" type="account-book" />
                <IntlMessages id="sidebar.receptionist.Accounts" />
              </Link>
            </Menu.Item>
            {showLegacyAccounts && (
              <Menu.Item
                key="receptionist/accounts-legacy">
                <Link to="/receptionist/accounts-legacy">
                  <Icon className="custom-sidebar-icon" type="account-book" />
                  <IntlMessages id="sidebar.receptionist.AccountsLegacy" />
                </Link>
              </Menu.Item>
            )}
            <Menu.Item key="profile/settings">
              <Link to="/receptionist/profile-settings">
                <SettingsIcon />
                <IntlMessages id="sidebar.receptionist.profileSettings" />
              </Link>
            </Menu.Item>
            <Menu.Item className="logout-item" key="profile/logout" onClick={handleLogout}>
                <LogoutIcon />
                <span>Logout</span>
            </Menu.Item>
          </ItemGroup>
        </Menu>
      );
    }

    // if (authUser?.role === "MRT") {
    //   return (
    //     <Menu
    //       defaultOpenKeys={[defaultOpenKeys]}
    //       selectedKeys={[selectedKeys]}
    //       theme={themeType === THEME_TYPE_LITE ? "light" : "dark"}
    //       mode="inline"
    //     >
    //       <Menu.Item key="mrt/worklist">
    //         <Link to="/mrt/worklist">
    //           <TechnicianWorklistIcon />
    //           <IntlMessages id="sidebar.receptionist.Worklist" />
    //         </Link>
    //       </Menu.Item>
    //     </Menu>
    //   );
    // }

    return (
      <Menu
        theme={themeType === THEME_TYPE_LITE ? "light" : "dark"}
        mode="inline"
      >
        <ItemGroup
          key="unauthorized"
          title={<IntlMessages id="sidebar.unauthorized" />}
        />
      </Menu>
    );
  };

  return (
    <Auxiliary>
      <SidebarLogo />
      <div id="custom-sidebar-content" className="gx-sidebar-content">
        <CustomScrollbars
          id="custom-sidebar"
          className="gx-layout-sider-scrollbar"
        >
          {renderMenu()}
        </CustomScrollbars>

        {/* 6. Place the Banner here */}
        <WhatsNewBanner 
          isManualOpen={isNewsOpen} 
          onClose={() => setIsNewsOpen(false)} 
        />

        {/* <div className="clarity-icon">
          <img alt="clarity-icon" src={require("assets/images/clarity-icon.png")} />
        </div> */}
      </div>
      <div className="sidebar-footer">
        <p className="dark-mode-toggle">
          
          <Switch
            checked={themeType === THEME_TYPE_DARK}
            onChange={onThemeChange}
            checkedChildren="Dark Mode"
            unCheckedChildren="Light Mode"
            className="custom-switch"
          />
        </p>
      </div>
      <ChatbotButton />
    </Auxiliary>
  );
};

export default SidebarContent;
